/* Leonparc product gallery: thumbnail choice, arrows, touch swipe, accessible zoom,
   Shopify variant-associated media and conservative imported-description cleanup. */
(() => {
  'use strict';
  const clampIndex = (value, length) => (value % length + length) % length;

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.stage = this.querySelector('[data-gallery-stage]');
      this.modal = this.querySelector('[data-gallery-dialog]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.zoomCounter = this.querySelector('[data-gallery-zoom-counter]');
      this.zoomImage = this.querySelector('[data-gallery-zoom-image]');
      this.imageSlides = this.slides.filter(slide => slide.querySelector('[data-gallery-zoom-open]'));
      this.activeIndex = 0;
      this.zoomIndex = 0;
      this.lastZoomOpener = null;
      this.pointerStart = null;
      this.suppressClickUntil = 0;
      this.select(0);
      const signal = this.controller.signal;

      this.addEventListener('click', (event) => {
        const control = event.target.closest('[data-gallery-prev],[data-gallery-next],[data-gallery-thumb],[data-gallery-zoom-open],[data-gallery-zoom-prev],[data-gallery-zoom-next],[data-gallery-zoom-close]');
        if (!control || !this.contains(control)) return;
        if (control.matches('[data-gallery-prev]')) this.select(this.activeIndex - 1);
        else if (control.matches('[data-gallery-next]')) this.select(this.activeIndex + 1);
        else if (control.matches('[data-gallery-thumb]')) this.select(Number(control.dataset.index));
        else if (control.matches('[data-gallery-zoom-open]')) {
          if (Date.now() < this.suppressClickUntil) { event.preventDefault(); return; }
          if (this.modal && typeof this.modal.showModal === 'function') {
            event.preventDefault();
            this.openZoom(control);
          }
        } else if (control.matches('[data-gallery-zoom-prev]')) this.changeZoom(-1);
        else if (control.matches('[data-gallery-zoom-next]')) this.changeZoom(1);
        else if (control.matches('[data-gallery-zoom-close]')) this.modal?.close();
      }, { signal });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target.closest('input,textarea,select,video,model-viewer')) return;
        if (this.modal?.open) {
          event.preventDefault();
          this.changeZoom(event.key === 'ArrowRight' ? 1 : -1);
          return;
        }
        if (this.slides.length < 2 || !event.target.closest('[data-gallery-stage],[data-gallery-thumb]')) return;
        event.preventDefault();
        this.select(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });

      this.stage?.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }, { signal, passive: true });
      this.stage?.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'touch' || !this.pointerStart || this.slides.length < 2) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY) * 1.3) return;
        this.suppressClickUntil = Date.now() + 350;
        this.select(this.activeIndex + (deltaX < 0 ? 1 : -1));
      }, { signal, passive: true });

      this.modal?.addEventListener('close', () => {
        if (this.zoomImage) this.zoomImage.removeAttribute('src');
        const target = this.lastZoomOpener;
        if (target?.isConnected && !target.closest('[hidden]')) target.focus({ preventScroll: true });
        else this.querySelector('[data-gallery-next]')?.focus({ preventScroll: true });
      }, { signal });
      this.addEventListener('leonparc:variant-media', (event) => {
        const id = String(event.detail?.id || '');
        if (!id) return;
        const index = this.slides.findIndex(slide => slide.dataset.mediaId === id);
        if (index >= 0) this.select(index);
      }, { signal });
    }
    disconnectedCallback() { this.controller?.abort(); }

    select(rawIndex) {
      if (!this.slides.length) return;
      const nextIndex = clampIndex(Number.isFinite(rawIndex) ? rawIndex : 0, this.slides.length);
      const previous = this.slides[this.activeIndex];
      if (previous && this.activeIndex !== nextIndex) {
        previous.querySelectorAll('video').forEach(video => video.pause());
      }
      this.activeIndex = nextIndex;
      for (const [i, slide] of this.slides.entries()) slide.hidden = i !== nextIndex;
      for (const thumb of this.thumbs) {
        const active = Number(thumb.dataset.index) === nextIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      }
      if (this.counter) this.counter.textContent = (nextIndex + 1) + ' / ' + this.slides.length;
      const selected = this.thumbs[nextIndex];
      if (selected && this.thumbs.length > 3) selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    openZoom(opener) {
      const selected = this.slides[this.activeIndex];
      const index = this.imageSlides.indexOf(selected);
      if (index < 0 || !this.modal) return;
      this.zoomIndex = index;
      this.lastZoomOpener = opener;
      this.updateZoom();
      this.modal.showModal();
      this.querySelector('[data-gallery-zoom-close]')?.focus();
    }
    changeZoom(delta) {
      if (!this.imageSlides.length) return;
      this.zoomIndex = clampIndex(this.zoomIndex + delta, this.imageSlides.length);
      this.updateZoom();
      const slide = this.imageSlides[this.zoomIndex];
      const index = this.slides.indexOf(slide);
      if (index >= 0 && index !== this.activeIndex) this.select(index);
    }
    updateZoom() {
      const current = this.imageSlides[this.zoomIndex];
      if (!current || !this.zoomImage) return;
      const anchor = current.querySelector('[data-gallery-zoom-open]');
      const image = anchor?.querySelector('img');
      if (!anchor) return;
      this.zoomImage.src = anchor.href;
      this.zoomImage.alt = image?.alt || '';
      if (this.zoomCounter) this.zoomCounter.textContent = 'Foto ' + (this.zoomIndex + 1) + ' van ' + this.imageSlides.length;
    }
  }
  if (!customElements.get('lp-product-gallery')) customElements.define('lp-product-gallery', ProductGallery);

  function refineDescriptions(scope = document) {
    scope.querySelectorAll('.lp-product[data-product]').forEach(product => {
      const content = product.querySelector('[data-product-description]');
      if (!content) return;
      const rawTitle = product.querySelector('[data-product-title]')?.textContent?.trim() || '';
      const normalize = value => (value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('nl');
      const title = normalize(rawTitle);
      const headings = [...content.querySelectorAll('h1, h2')];
      for (const heading of headings) {
        if (normalize(heading.textContent) === title && !heading.querySelector('img')) {
          heading.hidden = true;
          heading.setAttribute('aria-hidden', 'true');
        }
      }
      const teaser = product.querySelector('[data-product-intro]');
      if (teaser) {
        const firstUsefulParagraph = [...content.querySelectorAll('p')].map(item => item.textContent.trim())
          .find(text => text.length >= 65 && normalize(text) !== title);
        if (firstUsefulParagraph) {
          const trimmed = firstUsefulParagraph.length > 210
            ? firstUsefulParagraph.slice(0, 208).replace(/\s+\S*$/, '') + '…'
            : firstUsefulParagraph;
          teaser.textContent = trimmed;
          teaser.hidden = false;
        }
      }
    });
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const stepper = event.target.closest('[data-product-quantity-step]');
    if (!stepper) return;
    const input = stepper.closest('.lp-product__qty-group')?.querySelector('input[type=number]');
    if (!input) return;
    const amount = Number(stepper.dataset.productQuantityStep);
    const current = Number.parseInt(input.value, 10);
    const next = Math.max(1, (Number.isFinite(current) && current >= 1 ? current : 1) + amount);
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', event => {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    if (!control.matches('[data-product-variant]') || (control instanceof HTMLInputElement && !control.checked)) return;
    const selected = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
    const price = control.closest('[data-product]')?.querySelector('[data-product-price]');
    if (price && selected?.dataset.priceHtml) price.innerHTML = selected.dataset.priceHtml;
  });

  const scan = (scope) => refineDescriptions(scope);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target instanceof Element ? event.target : document));
})();
